import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
  GPX_FEATURES,
} from '@rsos/gainsight';

// Event names specific to TF911
const TF911_EVENT_NAMES = {
  START: 'start_tf911_chat',
  START_RESPONSE: 'start_tf911_chat_response',
  END: 'end_tf911_chat',
  END_MODAL_CONFIRMED: 'end_tf911_chat_modal_confirmed',
  END_MODAL_CANCELLED: 'end_tf911_chat_modal_cancelled',
  END_RESPONSE: 'end_tf911_chat_response',
  SEND_PRECONFIGURED_MESSAGE: 'send_tf911_preconfigured_message',
  SHOW_PRECONFIGURED_MESSAGES: 'show_tf911_preconfigured_messages',
  HIDE_PRECONFIGURED_MESSAGES: 'hide_tf911_preconfigured_messages',
  SHOW_CHAT_BOX: 'show_tf911_chat_box',
  HIDE_CHAT_BOX: 'hide_tf911_chat_box',
  SHOW_OUTBOUND_SMS_CARD: 'show_outbound_sms_card',
  HIDE_OUTBOUND_SMS_CARD: 'hide_outbound_sms_card',
  SEND_OUTBOUND_SMS: 'send_outbound_sms',
  SHOW_LIVE_CHAT: 'show_tf911_live_chat',
};

/**
 * Tracks when a user initiates a TF911 chat session
 * @param {Object} params - The parameters for tracking the chat initiation
 * @param {string} params.feature - The feature identifier
 * @param {boolean} params.fromOverviewTab - Whether the action was initiated from the overview tab
 * @param {boolean} params.isUniteEventPaneEnabled - Whether the Unite event pane is enabled
 */
export const trackStartTF911Click = ({
  feature,
  fromOverviewTab,
  isUniteEventPaneEnabled,
}) => {
  gspx.trackEvent({
    name: TF911_EVENT_NAMES.START,
    type: GPX_EVENT_TYPES.CLICK,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    additionalKeys: {
      overview_tab: isUniteEventPaneEnabled ? fromOverviewTab : undefined,
    },
  });
};

/**
 * Tracks a response when initiating a TF911 chat session
 * @param {Object} params - The parameters for tracking the chat initiation response
 * @param {string} params.feature - The feature identifier
 * @param {boolean} params.responseSuccess - Whether the response was successful
 * @param {string} params.conversationId - The conversation ID
 */
export const trackStartTF911Response = ({
  feature,
  responseSuccess,
  conversationId,
}) => {
  gspx.trackEvent({
    name: TF911_EVENT_NAMES.START_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
    additionalKeys: {
      success: responseSuccess,
    },
  });
};

/**
 * Tracks when a user clicks the end TF911 chat button
 * @param {Object} params - The parameters for tracking the end chat action
 * @param {string} params.feature - The feature identifier
 * @param {boolean} params.fromOverviewTab - Whether the action was initiated from the overview tab
 * @param {boolean} params.isUniteEventPaneEnabled - Whether the Unite event pane is enabled
 * @param {string} params.conversationId - The conversation ID
 */
export const trackEndTF911Click = ({
  feature,
  fromOverviewTab,
  isUniteEventPaneEnabled,
  conversationId,
}) => {
  gspx.trackEvent({
    name: TF911_EVENT_NAMES.END,
    type: GPX_EVENT_TYPES.CLICK,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
    additionalKeys: {
      overview_tab: isUniteEventPaneEnabled ? fromOverviewTab : undefined,
    },
  });
};

/**
 * Tracks when a user interacts with the end TF911 chat confirmation modal
 * @param {Object} params - The parameters for tracking the modal interaction
 * @param {string} params.feature - The feature identifier
 * @param {boolean} params.fromOverviewTab - Whether the action was initiated from the overview tab
 * @param {boolean} params.isUniteEventPaneEnabled - Whether the Unite event pane is enabled
 * @param {boolean} params.isConfirmed - Whether the user confirmed or cancelled the end chat action
 * @param {string} params.conversationId - The conversation ID
 */
export const trackEndTF911ModalClick = ({
  feature,
  fromOverviewTab,
  isUniteEventPaneEnabled,
  isConfirmed,
  conversationId,
}) => {
  gspx.trackEvent({
    name: isConfirmed
      ? TF911_EVENT_NAMES.END_MODAL_CONFIRMED
      : TF911_EVENT_NAMES.END_MODAL_CANCELLED,
    type: GPX_EVENT_TYPES.CLICK,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
    additionalKeys: {
      overview_tab: isUniteEventPaneEnabled ? fromOverviewTab : undefined,
    },
  });
};

/**
 * Tracks the response event when ending a Text-From-911 conversation.
 *
 * @param {Object} params - The parameters object
 * @param {string} params.feature - The feature identifier being tracked
 * @param {boolean} params.responseSuccess - Indicates if the end response was successful
 * @param {string} params.conversationId - The unique identifier for the TF911 conversation
 */
export const trackEndTF911Response = ({
  feature,
  responseSuccess,
  conversationId,
}) => {
  gspx.trackEvent({
    name: TF911_EVENT_NAMES.END_RESPONSE,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
    additionalKeys: {
      success: responseSuccess,
    },
  });
};

/**
 * Tracks when a user clicks to send a preconfigured message.
 * @param {Object} params - The parameters object
 * @param {string} params.feature - The feature identifier being tracked
 * @param {boolean} params.fromOverviewTab - Indicates if action was taken from overview tab
 * @param {boolean} params.isUniteEventPaneEnabled - Flag indicating if Unite event pane is enabled
 * @param {number} params.messageIndex - Index of the preconfigured message being sent
 * @param {string} params.message - The actual message content being sent
 */
export const trackSendPreconfiguredMessageClick = ({
  feature,
  fromOverviewTab,
  isUniteEventPaneEnabled,
  messageIndex,
  message,
}) => {
  gspx.trackEvent({
    name: TF911_EVENT_NAMES.SEND_PRECONFIGURED_MESSAGE,
    type: GPX_EVENT_TYPES.CLICK,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      message_index: messageIndex,
      message,
    },
    additionalKeys: {
      overview_tab: isUniteEventPaneEnabled ? fromOverviewTab : undefined,
    },
  });
};

/**
 * Tracks when a user toggles the TF911 accordion
 * @param {Object} params - The parameters for tracking the accordion toggle
 * @param {boolean} params.isExpanded - Whether the accordion is being expanded or collapsed
 * @param {string} params.feature - The feature identifier
 * @param {boolean} params.fromOverviewTab - Whether the action was initiated from the overview tab
 * @param {boolean} params.isUniteEventPaneEnabled - Whether the Unite event pane is enabled
 */
export const trackTogglingTF911Accordion = ({
  isExpanded,
  feature,
  fromOverviewTab,
  isUniteEventPaneEnabled,
}) => {
  gspx.trackEvent({
    name: isExpanded
      ? TF911_EVENT_NAMES.SHOW_PRECONFIGURED_MESSAGES
      : TF911_EVENT_NAMES.HIDE_PRECONFIGURED_MESSAGES,
    type: GPX_EVENT_TYPES.CLICK,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    additionalKeys: {
      overview_tab: isUniteEventPaneEnabled ? fromOverviewTab : undefined,
    },
  });
};

/**
 * Tracks when a user toggles the TF911 chat box
 * @param {Object} params - The parameters for tracking the chat box toggle
 * @param {boolean} params.isExpanded - Whether the chat box is being expanded or collapsed
 * @param {string} params.feature - The feature identifier
 * @param {boolean} params.fromOverviewTab - Whether the action was initiated from the overview tab
 * @param {boolean} params.isUniteEventPaneEnabled - Whether the Unite event pane is enabled
 * @param {string} params.conversationId - The ID of the conversation
 */
export const trackTogglingTF911ChatBox = ({
  isExpanded,
  feature,
  fromOverviewTab,
  isUniteEventPaneEnabled,
  conversationId,
}) => {
  gspx.trackEvent({
    name: isExpanded
      ? TF911_EVENT_NAMES.SHOW_CHAT_BOX
      : TF911_EVENT_NAMES.HIDE_CHAT_BOX,
    type: GPX_EVENT_TYPES.CLICK,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
    additionalKeys: {
      overview_tab: isUniteEventPaneEnabled ? fromOverviewTab : undefined,
    },
  });
};

/**
 * Tracks when a user toggles the outbound SMS card
 * @param {Object} params - The parameters for tracking the outbound SMS card toggle
 * @param {boolean} params.show - Whether the card is being shown or hidden
 * @param {boolean} [params.isMapCardCloseBtn=false] - Whether the toggle was triggered by closing the map card
 */
export const trackTogglingOutboundSMSCard = ({ show, isMapCardCloseBtn }) => {
  gspx.trackEvent({
    name: show
      ? TF911_EVENT_NAMES.SHOW_OUTBOUND_SMS_CARD
      : TF911_EVENT_NAMES.HIDE_OUTBOUND_SMS_CARD,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.TF911_OUTBOUND,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    additionalKeys: {
      map_card_close_btn: isMapCardCloseBtn,
    },
  });
};

/**
 * Tracks when a user clicks to send an outbound SMS
 */
export const trackSendOutboundSMSClick = () => {
  gspx.trackEvent({
    name: TF911_EVENT_NAMES.SEND_OUTBOUND_SMS,
    type: GPX_EVENT_TYPES.CLICK,
    feature: GPX_FEATURES.TF911_OUTBOUND,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
  });
};

/**
 * Tracks when a user clicks on a live TF911 card
 * @param {Object} params - The parameters for tracking the card click
 * @param {string} params.feature - The feature identifier
 * @param {string} params.conversationId - The ID of the conversation
 */
export const trackLiveTF911CardClick = ({ feature, conversationId }) => {
  gspx.trackEvent({
    name: TF911_EVENT_NAMES.SHOW_LIVE_CHAT,
    type: GPX_EVENT_TYPES.CLICK,
    feature,
    capability: GPX_CAPABILITIES.TF911,
    route: GPX_ROUTES.COMMUNICATOR,
    entities: {
      conversation_id: conversationId,
    },
  });
};
